import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getPosts, getFeaturedPosts, getCategories } from "../../../utilities/wp";
import Header from "../../../components/header/header";
import Footer from "../../../components/footer/footer";
import "./blog.css";
import { useTranslation } from "react-i18next";
import FeaturedPostsCarousel from "../../../components/blogComponents/FeaturedPostsCarousel";

const Blog = () => {
    function stripHtml(html = "") {
        const noTags = String(html).replace(/<[^>]*>/g, "").trim();

        const decodeOnce = (s) => {
            const txt = document.createElement("textarea");
            txt.innerHTML = s;
            return txt.value;
        };

        // Decode 2 veces para casos tipo &amp;#8220;
        return decodeOnce(decodeOnce(noTags)).replace(/\s+/g, " ").trim();
    }

    function formatDate(dateStr) {
        try {
        return new Date(dateStr).toLocaleDateString("es-CO", {
            year: "numeric",
            month: "short",
            day: "2-digit",
        });
        } catch {
        return dateStr;
        }
    }

    function getFeaturedImage(post) {
        const media = post?._embedded?.["wp:featuredmedia"]?.[0];
        return media?.source_url || null;
    }

    function getPaginationItems(current, total) {
        // 1 … 4 5 6 … 12
        const delta = 1;
        const range = [];
        const rangeWithDots = [];

        if (total <= 1) return [1];

        const left = Math.max(2, current - delta);
        const right = Math.min(total - 1, current + delta);

        range.push(1);
        for (let i = left; i <= right; i++) range.push(i);
        range.push(total);

        const unique = [...new Set(range)].sort((a, b) => a - b);

        for (let i = 0; i < unique.length; i++) {
        const curr = unique[i];
        const prev = unique[i - 1];

        if (i > 0 && curr - prev > 1) rangeWithDots.push("…");
        rangeWithDots.push(curr);
        }

        return rangeWithDots;
    }

    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [refreshKey, setRefreshKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [totalPages, setTotalPages] = useState(1);
    const { t } = useTranslation();
    const BLOG_BANNER = `${process.env.PUBLIC_URL}/img/banner-blog2.jpg`;
    const [featured, setFeatured] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryId, setCategoryId] = useState("");
    const [search, setSearch] = useState("");
    const [searchDraft, setSearchDraft] = useState(""); 
    const location = useLocation();


    const perPage = 6;

    useEffect(() => {
        let mounted = true;

        (async () => {
            setLoading(true);
            setError("");

            try {
            const result = await getPosts({
                page,
                perPage,
                search,                 
                categoryId: categoryId,
            });

            const data = Array.isArray(result) ? result : result.posts;
            const tp = Array.isArray(result) ? 1 : result.totalPages;

            if (mounted) {
                setPosts(data);
                setTotalPages(tp || 1);
            }
            } catch (e) {
            if (mounted) setError(e?.message || t("BLOG_POST_ERROR"));
            } finally {
            if (mounted) setLoading(false);
            }
        })();

        return () => (mounted = false);
    }, [page, refreshKey, search, categoryId]); 


    useEffect(() => {
        getFeaturedPosts({ perPage: 6 }).then(setFeatured);
    }, []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
            const cats = await getCategories();
            if (mounted) setCategories(cats);
            } catch (e) {
            // opcional: mostrar error suave, no bloquear la página
            console.warn("No se pudieron cargar categorías", e);
            }
        })();
        return () => (mounted = false);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const catFromUrl = params.get("cat") || "";
        const qFromUrl = params.get("q") || "";

        // Solo setea si cambia, para evitar renders extra
        if (catFromUrl !== String(categoryId)) {
            setCategoryId(catFromUrl);
            setPage(1);
        }

        // opcional: si también quieres que llegue búsqueda desde URL
        if (qFromUrl !== search) {
            setSearch(qFromUrl);
            setSearchDraft(qFromUrl);
            setPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);



    return (
        <>
        <Header />

        <div className="blog-page">
            <div className="blog-hero" role="banner" aria-label={"Blog banner"} style={{ backgroundImage: `url(${BLOG_BANNER})`}}>
                <div className="blog-hero-overlay">
                    <h1 className="blog-hero-title">{t("BLOG_HERO_TITLE")}</h1>
                </div>
            </div>

            {featured.length > 0 && (
                <section className="blog-section">
                    <div className="blog-container">
                        <h2 className="blog-section-title">{t("BLOG_FEATURED_TITLE")}</h2>
                        <FeaturedPostsCarousel posts={featured} />
                    </div>
                </section>
            )}


            <div className="blog-container blog-container--tight">

                {loading && (
                    <div className="blog-loader" role="status" aria-live="polite">
                        <span className="blog-spinner" aria-hidden="true" />
                        <span className="blog-loader-text">{t("BLOG_POST_LOADING")}</span>
                    </div>
                )}


                {!loading && error && (
                    <div className="blog-error">
                    <p className="blog-error-title">{t("BLOG_POST_ERROR")} 😵‍💫</p>
                    <p className="blog-error-text">{error}</p>
                    <button
                        className="blog-retry-btn"
                        onClick={() => setRefreshKey((k) => k + 1)}
                    >
                        Reintentar
                    </button>
                    </div>
                )}

                {!loading && !error && posts.length === 0 && (
                    <p className="blog-empty">{t("BLOG_POST_EMPTY")} </p>
                )}
                
                
                {!loading && !error && posts.length > 0 && (
                    <>
                    <h2 className="blog-section-title blog-all-title">
                        {t("BLOG_ALL_TITLE")}
                    </h2>
                    {/* Barra de búsqueda y filtros */}
                        <section className="blog-filters">
                            <div className="blog-container blog-filters-row">
                                <div className="blog-filter">
                                <label>{t("BLOG_FILTER_CATEGORY")}</label>
                                <select
                                    value={categoryId}
                                    onChange={(e) => {
                                    setCategoryId(e.target.value);
                                    setPage(1);
                                    }}
                                >
                                    <option value="">{t("BLOG_FILTER_ALL")}</option>
                                    {categories.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name} ({c.count})
                                    </option>
                                    ))}
                                </select>
                                </div>

                                <div className="blog-filter">
                                <label>{t("BLOG_FILTER_SEARCH")}</label>
                                <input
                                    value={searchDraft}
                                    onChange={(e) => setSearchDraft(e.target.value)}
                                    placeholder={t("BLOG_FILTER_SEARCH_PH")}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                        e.preventDefault();
                                        setPage(1);
                                        setSearch(searchDraft.trim()); 
                                        }
                                    }}
                                />

                                </div>

                                {(search || categoryId) && (
                                <button
                                    className="blog-filter-clear"
                                    onClick={() => {
                                        setSearch("");
                                        setCategoryId("");
                                        setPage(1);
                                        setSearchDraft("");
                                    }}
                                >
                                    {t("BLOG_FILTER_CLEAR")}
                                </button>
                                )}
                            </div>
                        </section>

                    {/* Todos los artículos*/}                    
                    <div className="blog-grid">
                        {posts.map((post) => {
                        const slug = post.slug;
                        const title = stripHtml(post.title.rendered);
                        const excerpt = stripHtml(post.excerpt.rendered);
                        const date = formatDate(post.date);
                        const img = getFeaturedImage(post);

                        return (
                            <article key={post.id} className="blog-card">
                            {img && (
                                <div className="blog-card-image">
                                <img src={img} alt={title} loading="lazy" />
                                </div>
                            )}

                            <div className="blog-card-content">
                                <p className="blog-card-date">{date}</p>
                                <h2 className="blog-card-title">{title}</h2>
                                <p className="blog-card-excerpt">
                                {excerpt.slice(0, 140)}
                                {excerpt.length > 140 && "…"}
                                </p>

                                <Link to={`/blog/${slug}`} className="blog-card-link">
                                    {t("BLOG_READ_MORE")} →
                                </Link>
                            </div>
                            </article>
                        );
                        })}
                    </div>

                    {/* Paginación numérica */}
                    <div className="blog-pagination">
                        <button
                        className="blog-page-btn"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        aria-label="Página anterior"
                        >
                        ←
                        </button>

                        <div className="blog-page-numbers">
                        {getPaginationItems(page, totalPages).map((item, idx) => {
                            if (item === "…") {
                            return (
                                <span key={`dots-${idx}`} className="blog-page-dots">
                                …
                                </span>
                            );
                            }

                            return (
                            <button
                                key={item}
                                className={`blog-page-number ${
                                item === page ? "is-active" : ""
                                }`}
                                onClick={() => setPage(item)}
                            >
                                {item}
                            </button>
                            );
                        })}
                        </div>

                        <button
                        className="blog-page-btn"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        aria-label="Página siguiente"
                        >
                        →
                        </button>
                    </div>
                    </>
                )}
                </div>
        </div>

        <Footer />
        </>
    );
};

export default Blog;
